import { createSelector } from '@ngrx/store';
import moment from 'moment-timezone';

import type {
  ReportComponentDataProps,
  ReportDashboardComponent,
  ReportDashboardComponentViewData,
  ReportDashboardWithReferences,
} from '@core/reports-types';
import { overrideComponentFilters } from '@core/reports-util';

import {
  ComponentDataPropsSelector,
  ReportsComponentsSelectors,
  selectComponentDataParamsFactory,
} from './components/components.store';
import { ReportsDashboardsSelectors } from './dashboards/dashboards.store';
import { ReportsMetricsSelectors } from './metrics/metrics.store';

const selectComponentDataPropsWithDashboardFiltersApplied = (
  componentId: number | string,
  dashboardId: number | string,
) =>
  createSelector(
    ReportsComponentsSelectors.componentDataProps(componentId),
    ReportsDashboardsSelectors.dashboardDataProps(dashboardId),
    ReportsDashboardsSelectors.accountCreatedAt,
    (component, dashboard, accountCreatedAt): ReportComponentDataProps | null =>
      component && dashboard
        ? overrideComponentFilters(
            component,
            dashboard,
            accountCreatedAt ? moment(accountCreatedAt) : moment(0),
          )
        : null,
  );

const selectComponentDataParamsWithDashboardFiltersApplied = (
  componentId: string | number,
  dashboardId: string | number,
) =>
  selectComponentDataParamsFactory(
    selectComponentDataPropsWithDashboardFiltersApplied(componentId, dashboardId),
  )(componentId);

const createDataParamsForComponent = (
  component: ReportDashboardComponentViewData | ReportDashboardComponent,
) => {
  let componentSelector: ComponentDataPropsSelector;
  if ('id' in component) {
    componentSelector = ReportsComponentsSelectors.componentDataProps(component.id);
  } else {
    componentSelector = createSelector(() => component);
  }

  return selectComponentDataParamsFactory(componentSelector)();
};

const selectComponent = (componentId: number | string, dashboardId?: number | string) =>
  createSelector(
    dashboardId
      ? selectComponentDataPropsWithDashboardFiltersApplied(componentId, dashboardId)
      : ReportsComponentsSelectors.componentDataProps(componentId),
    ReportsComponentsSelectors.componentVisualizationProps(componentId),
    ReportsComponentsSelectors.componentInfoProps(componentId),
    ReportsComponentsSelectors.componentStaticProps(componentId),
    ReportsComponentsSelectors.componentGridProps(componentId),
    (
      dataProps,
      visualizationProps,
      infoProps,
      staticProps,
      gridProps,
    ): ReportDashboardComponent | null =>
      dataProps && visualizationProps && infoProps && staticProps && gridProps
        ? {
            ...staticProps,
            ...infoProps,
            ...dataProps,
            ...visualizationProps,
            grid: gridProps,
            settings: {
              ...visualizationProps.settings,
              ...dataProps.settings,
            },
          }
        : null,
  );

const selectComponentMetric = (componentId: number | string) =>
  createSelector(
    ReportsComponentsSelectors.componentMetricSlug(componentId),
    ReportsMetricsSelectors.entityMap,
    (slug, metrics) => (slug && metrics && slug in metrics ? metrics[slug] : null),
  );

const selectDashboardComponents = (dashboardId: number) =>
  createSelector(
    ReportsComponentsSelectors.entityMap,
    ReportsComponentsSelectors.aliasesMap,
    ReportsDashboardsSelectors.byId(dashboardId),
    ReportsDashboardsSelectors.entityMetaMap,
    (components, aliases, dashboard, metaMap): ReportDashboardWithReferences | null => {
      if (!dashboard) {
        return null;
      }
      return {
        ...dashboard,
        position: (<any>metaMap[dashboard.id])?.position ?? 0,
        components: dashboard.componentIds.map(id => components[aliases[id] ?? id]!),
      };
    },
  );

export const ReportsSelectors = {
  component: selectComponent,
  componentDataPropsWithDashboardFiltersApplied:
    selectComponentDataPropsWithDashboardFiltersApplied,
  componentDataParamsWithDashboardFiltersApplied:
    selectComponentDataParamsWithDashboardFiltersApplied,
  componentMetric: selectComponentMetric,
  createDataParamsForComponent,
  selectDashboardComponents,
};
